import type { Database, Json } from "@base-hormonal/database";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";
import { validatePlan, type NutritionPlanPayload } from "../model/plans";

type PlanRow = Database["public"]["Tables"]["planos_alimentares"]["Row"];
type MealRow = Database["public"]["Tables"]["plano_refeicoes"]["Row"];
type ItemRow = Database["public"]["Tables"]["plano_refeicao_itens"]["Row"];
type ReplacementRow = Database["public"]["Tables"]["plano_item_substituicoes"]["Row"];
export type Anamnesis = Database["public"]["Tables"]["anamneses_nutricionais"]["Row"];

export interface NutritionPlan extends PlanRow {
  plano_refeicoes: (MealRow & {
    plano_refeicao_itens: (ItemRow & { plano_item_substituicoes: ReplacementRow[] })[];
  })[];
}

export const NUTRITION_PAGE_SIZE = 20;

const PLAN_COLUMNS =
  "id,aluno_id,user_id,nome,objetivo,meta_energia_kcal,meta_proteina_g,meta_carboidrato_g,meta_gordura_g,meta_fibra_g,metas_origem,metas_fonte,grupo_versao_id,versao,plano_anterior_id,status,data_inicio,data_fim_prevista,created_at,updated_at," +
  "plano_refeicoes!plano_refeicoes_plano_id_aluno_id_fkey(id,plano_id,nome,horario,observacoes,ordem," +
  "plano_refeicao_itens!plano_refeicao_itens_refeicao_id_aluno_id_fkey(id,refeicao_id,alimento,quantidade,unidade,calorias_kcal,proteina_g,carboidrato_g,gordura_g,valores_origem,valores_fonte,observacoes,ordem," +
  "plano_item_substituicoes!plano_item_substituicoes_item_id_aluno_id_fkey(id,item_id,alimento,quantidade,unidade,calorias_kcal,proteina_g,carboidrato_g,gordura_g,valores_origem,valores_fonte,observacoes,ordem)))";

const ANAMNESIS_COLUMNS =
  "id,aluno_id,user_id,objetivo,restricoes,alergias_intolerancias,preferencias,alimentos_evitar,rotina,refeicoes_por_dia,observacoes,created_at,updated_at";

export interface AnamnesisInput {
  aluno_id: string;
  objetivo: string | null;
  restricoes: string | null;
  alergias_intolerancias: string | null;
  preferencias: string | null;
  alimentos_evitar: string | null;
  rotina: string | null;
  refeicoes_por_dia: number | null;
  observacoes: string | null;
}

function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

/**
 * Escritor único de nutrição. O plano versionado e a troca de status passam
 * pelos RPCs transacionais; a anamnese é um upsert por perfil. A proveniência
 * de cada valor viaja no payload e nunca é inferida aqui.
 */
export function createNutritionApi(client: TypedSupabaseClient) {
  return {
    async plans(profileId: string, page = 0, signal?: AbortSignal): Promise<NutritionPlan[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("planos_alimentares")
          .select(PLAN_COLUMNS)
          .eq("aluno_id", profileId)
          .order("created_at", { ascending: false })
          .range(page * NUTRITION_PAGE_SIZE, (page + 1) * NUTRITION_PAGE_SIZE - 1)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as NutritionPlan[];
      });
    },

    async anamnesis(profileId: string, signal?: AbortSignal): Promise<Anamnesis | null> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("anamneses_nutricionais")
          .select(ANAMNESIS_COLUMNS)
          .eq("aluno_id", profileId)
          .abortSignal(activeSignal)
          .maybeSingle();
        raise(error);
        return data;
      });
    },

    async savePlan(
      profileId: string,
      payload: NutritionPlanPayload,
      baseId: string | null,
    ): Promise<string> {
      const errors = validatePlan(payload);
      if (errors.length > 0) throw new PublicAppError("invalid_input", errors[0] ?? "");
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .rpc("salvar_plano_alimentar_versionado", {
            p_aluno_id: profileId,
            p_payload: payload as unknown as Json,
            ...(baseId === null ? {} : { p_plano_base_id: baseId }),
          })
          .abortSignal(signal);
        raise(error);
        return String(data ?? "");
      });
    },

    async changeStatus(planId: string, status: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .rpc("alterar_status_plano_alimentar", { p_plano_id: planId, p_status: status })
          .abortSignal(signal);
        raise(error);
      });
    },

    async deletePlan(planId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("planos_alimentares")
          .delete()
          .eq("id", planId)
          .abortSignal(signal);
        raise(error);
      });
    },

    async saveAnamnesis(userId: string, input: AnamnesisInput): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("anamneses_nutricionais")
          .upsert({ ...input, user_id: userId }, { onConflict: "aluno_id" })
          .abortSignal(signal);
        raise(error);
      });
    },

    async deleteAnamnesis(anamnesisId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("anamneses_nutricionais")
          .delete()
          .eq("id", anamnesisId)
          .abortSignal(signal);
        raise(error);
      });
    },
  };
}

export type NutritionApi = ReturnType<typeof createNutritionApi>;
